import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiJson } from '@/lib/api';
import type {
  ProfessionalDirectoryProfile,
  ProfessionalDirectoryProfileListResponse,
} from '@/lib/types';

function AdminDirectoryProfilesPage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');

  const query = useQuery({
    queryKey: ['admin-directory-profiles', status],
    queryFn: () =>
      apiJson<ProfessionalDirectoryProfileListResponse>(
        getToken,
        `/v1/admin/directory-profiles?status=${status}`,
      ),
  });

  const approveMutation = useMutation({
    mutationFn: (profileId: number) =>
      apiJson<ProfessionalDirectoryProfile>(
        getToken,
        `/v1/admin/directory-profiles/${profileId}/approve`,
        { method: 'POST' },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-directory-profiles'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ profileId, reason }: { profileId: number; reason: string }) =>
      apiJson<ProfessionalDirectoryProfile>(
        getToken,
        `/v1/admin/directory-profiles/${profileId}/reject`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason }),
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-directory-profiles'] });
    },
  });

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-semibold text-gray-900'>Directory Profiles</h1>
        <p className='text-sm text-gray-500 mt-1'>
          Review professional listing submissions for the public coaches directory.
        </p>
      </div>
      <div className='flex flex-wrap gap-2'>
        {(['pending', 'approved', 'rejected'] as const).map((s) => (
          <button
            key={s}
            type='button'
            onClick={() => setStatus(s)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${
              status === s
                ? 'bg-harbor-50 text-primary border border-sky-200'
                : 'bg-white text-gray-600 border border-gray-200 hover:text-gray-800'
            }`}
          >
            {s[0].toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <p className='text-sm text-gray-500'>Loading…</p>
      ) : (query.data?.profiles ?? []).length === 0 ? (
        <p className='text-sm text-gray-500'>No profiles in this status.</p>
      ) : (
        <ul className='space-y-4'>
          {(query.data?.profiles ?? []).map((profile) => (
            <li key={profile.id} className='rounded-xl border border-gray-200 bg-white p-4'>
              <div className='flex flex-wrap items-start justify-between gap-3'>
                <div className='space-y-1'>
                  <h3 className='text-base font-semibold text-gray-900'>{profile.display_name}</h3>
                  <p className='text-sm text-gray-500'>
                    {profile.state} · {profile.specialties.join(', ')}
                  </p>
                  {profile.headline ? (
                    <p className='text-sm text-gray-700'>{profile.headline}</p>
                  ) : null}
                  <p className='text-sm text-gray-600'>{profile.bio}</p>
                  <a
                    href={profile.website_url}
                    target='_blank'
                    rel='noreferrer'
                    className='text-sm text-primary hover:underline'
                  >
                    {profile.website_url}
                  </a>
                  {profile.rejection_reason ? (
                    <p className='text-sm text-red-700'>Rejection reason: {profile.rejection_reason}</p>
                  ) : null}
                </div>
                {status === 'pending' ? (
                  <div className='flex gap-2'>
                    <button
                      type='button'
                      onClick={() => approveMutation.mutate(profile.id)}
                      disabled={approveMutation.isPending}
                      className='rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50'
                    >
                      Approve
                    </button>
                    <button
                      type='button'
                      onClick={() => {
                        const reason = window.prompt(
                          'Reason for rejection (shown to professional):',
                          'Please add more detail to your bio and specialties.',
                        );
                        if (!reason) return;
                        rejectMutation.mutate({ profileId: profile.id, reason });
                      }}
                      disabled={rejectMutation.isPending}
                      className='rounded-lg border border-red-300 px-3 py-1.5 text-sm font-semibold text-red-700 disabled:opacity-50'
                    >
                      Reject
                    </button>
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/directory-profiles')({
  component: AdminDirectoryProfilesPage,
});
